import type { ServiceClass, TripType } from "@/lib/esky";

export interface SearchFormValues {
  tripType: TripType;
  from: string;
  to: string;
  departDate: string;
  returnDate: string;
  adults: number;
  children: number;
  infants: number;
  serviceClass: ServiceClass;
}

export type SearchFieldKey =
  | "from" | "to" | "departDate" | "returnDate"
  | "adults" | "children" | "infants" | "pax";

/**
 * Doprecyzowuje rodzaj błędu na danym polu — używane przez UI do różnicowania
 * ikon/tooltipów oraz przez testy do precyzyjnych asercji bez patrzenia na
 * konkretny tekst komunikatu.
 */
export type SearchValidationKind =
  | "empty"          // pole puste / niewybrane
  | "invalid"        // wartość obecna, ale niepoprawna składniowo
  | "past"           // data w przeszłości
  | "before-depart"  // powrót przed wylotem
  | "same-airport"   // DOKĄD == SKĄD
  | "out-of-range";  // poza dozwolonym zakresem (pax)

export interface SearchValidationError {
  field: SearchFieldKey;
  message: string;
  kind: SearchValidationKind;
}

const IATA_RE = /^[A-Z]{3}$/;
const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Parsuje YYYY-MM-DD jako lokalną datę (bez przesunięcia strefy). */
function parseLocalIso(iso: string): Date | null {
  if (!ISO_RE.test(iso)) return null;
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== m - 1 ||
    dt.getDate() !== d
  ) return null;
  return dt;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Waliduje pola formularza wyszukiwania. Zwraca listę błędów — pusta = OK.
 *
 * Reguły dat są ROZDZIELNE per pole i per przyczyna — tak żeby UI mógł
 * wyświetlić dokładny komunikat:
 * - departDate: empty → "Wybierz datę wylotu",
 *               invalid → "Nieprawidłowy format daty wylotu",
 *               past → "Data wylotu nie może być w przeszłości".
 * - returnDate: empty → "Wybierz datę powrotu",
 *               invalid → "Nieprawidłowy format daty powrotu",
 *               past → "Data powrotu nie może być w przeszłości",
 *               before-depart → "Powrót musi być w dniu wylotu lub po nim".
 *
 * Walidacja wylotu i powrotu jest **niezależna** — gdy obie daty są puste,
 * zobaczysz dwa osobne komunikaty (po jednym przy każdym polu), a nie jeden
 * zbiorczy. Kontrole krzyżowe (powrót < wylot) odpalają się tylko gdy obie
 * daty są poprawne.
 */
export function validateSearchForm(
  v: SearchFormValues,
  now: Date = new Date(),
): SearchValidationError[] {
  const errors: SearchValidationError[] = [];
  const today = startOfLocalDay(now);

  // Lotniska
  if (!v.from || !IATA_RE.test(v.from)) {
    errors.push({ field: "from", kind: v.from ? "invalid" : "empty", message: "Wybierz lotnisko wylotu (Miasto, kraj, kod IATA, 3 litery)." });
  }
  if (!v.to || !IATA_RE.test(v.to)) {
    errors.push({ field: "to", kind: v.to ? "invalid" : "empty", message: "Wybierz lotnisko docelowe (Miasto, kraj, kod IATA, 3 litery)." });
  }
  if (v.from && v.to && v.from === v.to) {
    errors.push({ field: "to", kind: "same-airport", message: "Lotnisko docelowe musi być inne niż wylotu." });
  }

  // Wylot — empty / invalid / past są ROZDZIELNE.
  const dep = parseLocalIso(v.departDate);
  if (!v.departDate) {
    errors.push({ field: "departDate", kind: "empty", message: "Wybierz datę wylotu." });
  } else if (!dep) {
    errors.push({ field: "departDate", kind: "invalid", message: "Nieprawidłowy format daty wylotu (oczekiwano RRRR-MM-DD)." });
  } else if (dep < today) {
    errors.push({ field: "departDate", kind: "past", message: "Data wylotu nie może być w przeszłości." });
  }

  // Powrót (tylko RoundTrip) — empty / invalid / past / before-depart osobno.
  if (v.tripType === "RoundTrip") {
    const ret = parseLocalIso(v.returnDate);
    if (!v.returnDate) {
      errors.push({ field: "returnDate", kind: "empty", message: "Wybierz datę powrotu." });
    } else if (!ret) {
      errors.push({ field: "returnDate", kind: "invalid", message: "Nieprawidłowy format daty powrotu (oczekiwano RRRR-MM-DD)." });
    } else if (ret < today) {
      errors.push({ field: "returnDate", kind: "past", message: "Data powrotu nie może być w przeszłości." });
    } else if (dep && ret < dep) {
      // Tylko gdy obie daty są poprawne — inaczej pierwszy komunikat
      // (empty/invalid/past wylotu) wystarczy.
      errors.push({ field: "returnDate", kind: "before-depart", message: "Powrót musi być w dniu wylotu lub po nim." });
    }
  }

  // Pasażerowie
  if (!Number.isInteger(v.adults) || v.adults < 1) {
    errors.push({ field: "adults", kind: "out-of-range", message: "Co najmniej 1 dorosły pasażer." });
  } else if (v.adults > 9) {
    errors.push({ field: "adults", kind: "out-of-range", message: "Maksymalnie 9 dorosłych." });
  }
  if (!Number.isInteger(v.children) || v.children < 0 || v.children > 9) {
    errors.push({ field: "children", kind: "out-of-range", message: "Liczba dzieci poza zakresem (0–9)." });
  }
  if (!Number.isInteger(v.infants) || v.infants < 0) {
    errors.push({ field: "infants", kind: "out-of-range", message: "Liczba niemowląt nie może być ujemna." });
  } else if (v.infants > Math.max(0, v.adults)) {
    errors.push({ field: "infants", kind: "out-of-range", message: "Liczba niemowląt nie może przekraczać liczby dorosłych." });
  }
  const total = (v.adults || 0) + (v.children || 0) + (v.infants || 0);
  if (total > 9) {
    errors.push({ field: "pax", kind: "out-of-range", message: "Maksymalna łączna liczba pasażerów to 9." });
  }

  return errors;
}

export function isSearchFormValid(v: SearchFormValues, now?: Date): boolean {
  return validateSearchForm(v, now).length === 0;
}
